import Plimit from 'p-limit';
import type {
  ShiptimeCancelRequest,
  ShiptimeCarrierLabel,
  ShiptimeLtlCostResponse,
  ShiptimeMegaShipmentProcessCreate,
  ShiptimeShipmentResponse,
} from '@shipium-corp/shipium-ts-types';
import type {
  CarrierSelection,
  DeliveryEstimateRequest,
  DeliveryEstimateResponse,
  SubscriptionTimingResponse,
  SubscriptionTimingRequest,
  ShipmentSearchRecord,
} from './types';
import GatewayAxiosClient from '../GatewayAxiosClient';
import { getToken } from '../../session';
import { shapeCancelHierarchy } from '../../shipments/utils';

export class ShiptimeService {
  public async getShipmentIdForExactMatch(shipiumId: string) {
    const result = await Promise.all([
      this.getShipmentByShipmentId(shipiumId).catch(() => null),
      this.getLabelByLabelId(shipiumId).catch(() => null),
    ]);
    return result[0]?.shipiumShipmentId || result[1]?.shipiumShipmentId || undefined;
  }

  public async getShipmentByShipmentId(shipmentId: string, partnerId = '') {
    const response = await GatewayAxiosClient.get<ShiptimeShipmentResponse>(
      `/shipment-ui-proxy/api/v1/deliveryexperience/shipment/${shipmentId}`,
      {
        params: {
          includeCancelled: true,
          includeLabelImagesInResponse: true,
        },
        headers: {
          'x-delegated-partner-id': partnerId,
        },
      },
    );

    return response.data;
  }
  public async getDeliveryEstimate(deid: string) {
    const response = await GatewayAxiosClient.get<DeliveryEstimateResponse>(
      `/shiptime-proxy/api/v1/deliveryexperience/productview/${deid}`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );

    return response.data;
  }
  public async getSubscriptionTiming(shipmentTimingId: string) {
    const response = await GatewayAxiosClient.get<SubscriptionTimingResponse>(
      `/shiptime-proxy/api/v1/subscription/timing/${shipmentTimingId}`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );

    return response.data;
  }
  public async createDeliveryEstimate(req: DeliveryEstimateRequest) {
    const response = await GatewayAxiosClient.post<DeliveryEstimateResponse>(
      `/shiptime-proxy/api/v1/deliveryexperience/productview`,
      req,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return response.data;
  }
  public async createSubscriptionTiming(req: SubscriptionTimingRequest) {
    const response = await GatewayAxiosClient.post<SubscriptionTimingResponse>(
      `/shiptime-proxy/api/v1/subscription/timing`,
      req,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return response.data;
  }
  public async getLabelByLabelId(labelId: string, includeLabelImagesInResponse = false) {
    const response = await GatewayAxiosClient.get<ShiptimeCarrierLabel>(
      `/shiptime-proxy/api/v1/deliveryexperience/shipment/carrierSelection/label/${labelId}?includeLabelImagesInResponse=${includeLabelImagesInResponse}`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return response.data;
  }

  public async getShipmentCarrierSelectionById(shipmentId: string, carrierSelectionId: string, partnerId = '') {
    const response = await GatewayAxiosClient.get<CarrierSelection>(
      `/shipment-ui-proxy/api/v1/deliveryexperience/shipment/${shipmentId}/carrierSelection/${carrierSelectionId}`,
      {
        params: {
          includeCancelled: true,
        },
        headers: {
          authorization: `Bearer ${getToken()}`,
          'x-delegated-partner-id': partnerId,
        },
      },
    );

    return response.data;
  }

  public async getShipmentLabel(url: string, packageReferenceIdentifier?: string): Promise<string> {
    const removeDomain = (urlString: string) => {
      // hack to fix urls
      // we only want to pass the path
      const res = urlString.indexOf('.com');
      return res > -1 ? urlString.slice(res + 5) : urlString;
    };
    const response = await GatewayAxiosClient.get(`/shiptime-proxy/${removeDomain(url)}`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  }

  public async cancelShipment(cancelPayload: ShiptimeCancelRequest): Promise<ShiptimeShipmentResponse> {
    const response = await GatewayAxiosClient.post(`/shiptime-proxy/api/v1/deliveryexperience/cancel`, cancelPayload);
    return response.data;
  }

  public async createLabel(labelRequest: ShiptimeMegaShipmentProcessCreate): Promise<ShiptimeShipmentResponse> {
    const response = await GatewayAxiosClient.post(
      `/shiptime-proxy/api/v1/shipment/carrierselection/label`,
      labelRequest,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );

    if (response?.data?.status === 201) {
      throw new Error(response?.data?.message);
    }

    const {
      shipiumShipmentId,
      carrierSelection: { carrierSelectionId },
      carrierLabel: { shipiumLabelId },
    } = response.data;

    if (labelRequest.labelParameters?.testMode === true) {
      try {
        const voidResponse = await this.cancelShipment({
          shipmentId: shipiumShipmentId,
          carrierSelectionId,
          shipiumLabelId,
        });
        response.data.carrierLabel.status = 'voided';
        response.data.carrierLabel.details.labelVoidDetails = voidResponse?.carrierLabel?.details?.labelVoidDetails;
      } catch (e) {
        console.error(e);
      }
    }

    return response.data;
  }

  public async fetchLtlCostById(LtlCostId: string): Promise<ShiptimeLtlCostResponse> {
    const response = await GatewayAxiosClient.get<ShiptimeLtlCostResponse>(
      `/shiptime-proxy/api/v1/ltl/shipment/cost/${LtlCostId}`,
    );
    return response.data;
  }

  public async bulkLabelVoid(request: Partial<ShipmentSearchRecord>[]) {
    const limit = Plimit(10);
    const promises = request.map((req) => {
      const { shipiumShipmentId, carrierSelectionId, shipiumLabelId } = req;
      return limit(() =>
        GatewayAxiosClient.post(
          `/shiptime-proxy/api/v1/deliveryexperience/cancel`,
          {
            cascadeCancel: true,
            ...shapeCancelHierarchy({
              shipiumShipmentId: shipiumShipmentId as string,
              carrierSelectionId,
              shipiumLabelId,
            }),
          },
          {},
        ),
      );
    });
    return await Promise.allSettled(promises);
  }
  public async fetchLabelZPL(labelId: string) {
    const response = await GatewayAxiosClient.get<string>(
      `/shiptime-proxy/api/v1/deliveryexperience/shipment/carrierSelection/label/${labelId}/zpl`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return response.data;
  }
}
